import React, { ReactElement, useState } from 'react';
import { MonitorIcon, SmartphoneIcon } from 'lucide-react';
import { useAppStateContext } from 'src/AppState/AppState';
import { observer } from 'src/utils/mobx-react';
import { MarkersOfHarmPopupPreviewContentType } from './MarkersOfHarmPopupTab.types';
import {
    PreviewActions,
    PreviewCloseButton,
    PreviewContent,
    PreviewDescription,
    PreviewDeviceButton,
    PreviewDeviceToggle,
    PreviewHeader,
    PreviewMobileContent,
    PreviewMobileDescription,
    PreviewMobileFrame,
    PreviewMobileHeader,
    PreviewMobileNotch,
    PreviewMobileOuter,
    PreviewMobilePrimaryButton,
    PreviewOverlay,
    PreviewPrimaryButton,
    PreviewSecondaryButton,
    PreviewTopBar,
} from './MarkersOfHarmPopupPreview.style';

type PreviewDeviceType = 'desktop' | 'mobile';

type MarkersOfHarmPopupPreviewPropsType = {
    content: MarkersOfHarmPopupPreviewContentType;
    onClose: () => void;
};

export const MarkersOfHarmPopupPreview = observer(
    'MarkersOfHarmPopupPreview',
    ({ content, onClose }: MarkersOfHarmPopupPreviewPropsType): ReactElement => {
        const { universeModule } = useAppStateContext();
        const theme = universeModule.themeColors;
        const CompliancePopupLogo = universeModule.compliancePopupLogo;
        const [device, setDevice] = useState<PreviewDeviceType>('desktop');

        const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
            event.preventDefault();
        };

        const hasButtons = content.primaryButton !== undefined || content.secondaryButton !== undefined;

        const isMobile = device === 'mobile';
        const TitleComponent = isMobile ? PreviewMobileHeader : PreviewHeader;
        const DescriptionComponent = isMobile ? PreviewMobileDescription : PreviewDescription;
        const PrimaryButtonComponent = isMobile ? PreviewMobilePrimaryButton : PreviewPrimaryButton;

        const popupBody = (
            <>
                {CompliancePopupLogo === null ? null : <CompliancePopupLogo />}
                {content.title === undefined ? null : (
                    <TitleComponent data-test='markers-of-harm-popup-preview-title'>{content.title}</TitleComponent>
                )}
                {content.bodyText === undefined ? null : (
                    <DescriptionComponent
                        theme={theme}
                        data-test='markers-of-harm-popup-preview-body'
                        dangerouslySetInnerHTML={{ __html: content.bodyText }}
                    />
                )}
                {hasButtons ? (
                    <PreviewActions data-test='markers-of-harm-popup-preview-actions'>
                        {content.primaryButton === undefined ? null : (
                            <PrimaryButtonComponent
                                type='button'
                                theme={theme}
                                data-test='markers-of-harm-popup-preview-primary-button'
                                onClick={handleButtonClick}
                            >
                                {content.primaryButton.text}
                            </PrimaryButtonComponent>
                        )}
                        {content.secondaryButton === undefined ? null : (
                            <PreviewSecondaryButton
                                type='button'
                                theme={theme}
                                data-test='markers-of-harm-popup-preview-secondary-button'
                                onClick={handleButtonClick}
                            >
                                {content.secondaryButton.text}
                            </PreviewSecondaryButton>
                        )}
                    </PreviewActions>
                ) : null}
            </>
        );

        return (
            <PreviewOverlay
                theme={theme}
                data-test='markers-of-harm-popup-preview-overlay'
            >
                <PreviewTopBar>
                    <PreviewDeviceToggle>
                        <PreviewDeviceButton
                            type='button'
                            isActive={device === 'desktop'}
                            onClick={(): void => setDevice('desktop')}
                            aria-label='Desktop preview'
                            data-test='markers-of-harm-popup-preview-device-desktop'
                        >
                            <MonitorIcon size={18} />
                        </PreviewDeviceButton>
                        <PreviewDeviceButton
                            type='button'
                            isActive={device === 'mobile'}
                            onClick={(): void => setDevice('mobile')}
                            aria-label='Mobile preview'
                            data-test='markers-of-harm-popup-preview-device-mobile'
                        >
                            <SmartphoneIcon size={18} />
                        </PreviewDeviceButton>
                    </PreviewDeviceToggle>
                    <PreviewCloseButton
                        type='button'
                        theme={theme}
                        data-test='markers-of-harm-popup-preview-close'
                        onClick={onClose}
                    >
                        Close preview
                    </PreviewCloseButton>
                </PreviewTopBar>

                {device === 'desktop' ? (
                    <PreviewContent data-test='markers-of-harm-popup-preview-content'>{popupBody}</PreviewContent>
                ) : (
                    <PreviewMobileOuter data-test='markers-of-harm-popup-preview-content-mobile'>
                        <PreviewMobileFrame theme={theme}>
                            <PreviewMobileNotch />
                            <PreviewMobileContent>{popupBody}</PreviewMobileContent>
                        </PreviewMobileFrame>
                    </PreviewMobileOuter>
                )}
            </PreviewOverlay>
        );
    }
);
